import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"

import { Form } from "react-final-form"
import { Box, Button, Text, Error } from "@mos-cat/ds"
import { usePageContext } from "@components/contexts/PageContext"
import { useZoneContext } from "@components/contexts/ZoneContext"
import { Editor, stringToEditorContent } from "@components/form"
import {
  PageBlockRow,
  PageBlockRowSection,
  PageBlockContent,
  CustomGetUsersOptionLabel,
  useAppContext,
  createValidation,
  runValidation,
} from "@dit/core-frontend"

import { Link } from "react-router-dom"
import isEqual from "lodash/isEqual"
import debounce from "lodash/debounce"
import { PAGE_STATUSES } from "@utils/pages"
import { useWebSocket } from "@src/hooks/useWebSocket"
import { Warning } from "@mos-cat/ds-icons"
import { ROUTES } from "@src/constants"
import { PageFormContextProvider, usePageFormContext } from "@components/contexts/PageFormContext"
import styles from "./pagePreview.module.css"

const validationSchema = createValidation({
  schema: yup.object().shape({
    title: yup
      .string()
      .required("Заголовок не должен быть пустым")
      .min(3, "Минимальное допустимое количество символов 3"),
  }),
})

// isSubmitting для того, что бы если нажать на обновление версии при задержке - не летел доп запрос
const debouncedUpdatePage = debounce(
  ({
    values,
    initialData,
    hasValidationErrors,
    updatePage,
    isSubmitting,
    isAutoSaveEnabled,
    shouldUpdateZonePages,
  }) => {
    if (isSubmitting || hasValidationErrors) {
      return
    }

    const newValues = { ...values }
    const initialValues = { ...initialData }

    delete newValues.plainText
    delete initialValues.plainText

    //повторная проверка на isAutoSaveEnabled
    //так как если обновление было запущено, то остановить его можно только тут
    //пока что isAutoSaveEnabled встает в true только в плагине автозамены
    if (!isEqual(initialValues, newValues) && isAutoSaveEnabled) {
      // В случае авто обновления нужно добавить к версии + 0.1,
      // но так, что бы версия не "округлилась
      const oldVersionArr = String(values.version).split(".")
      updatePage(
        {
          ...values,
          version: oldVersionArr[0] + "." + (Number(oldVersionArr[1] || 0) + 1),
        },
        false,
        null,
        shouldUpdateZonePages,
      )
    }
  },
  3000,
)

export const PageFormForm = memo(() => {
  const navigate = useNavigate()
  const { isAutoSaveEnabled } = usePageFormContext()
  const { page, updateErrors, statusList, updatePage, isUpdateLoading } = usePageContext()
  const { zone, addPage, togleTextCommentsShow } = useZoneContext()
  const { sseLink, user } = useAppContext()

  const [updateMessage, setUpdateMessage] = useState(null)
  const isServerValidationError = updateErrors.some(({ code }) => code === "validation_error")

  const isDraft = page.status.slug === PAGE_STATUSES.DRAFT
  const shouldUpdateZonePages = isDraft ? true : false

  const initialData = useMemo(() => {
    return {
      zone,
      title: page?.title || "",
      status: page?.status,
      plainText: page?.plainText || "",
      text: page?.text || "",
      pageId: page?.id,
      version: page?.version || "1",
      parent: page?.parent,
      isAutoSaveEnabled: true,
    }
  }, [page, zone])

  const onChangeFormValues = useCallback(
    (values, hasValidationErrors, isSubmitting, shouldUpdateZonePages) => {
      if (values.isAutoSaveEnabled) {
        debouncedUpdatePage({
          values,
          initialData,
          hasValidationErrors,
          updatePage,
          isSubmitting,
          isAutoSaveEnabled,
          shouldUpdateZonePages,
        })
      }
    },
    [initialData, updatePage, isAutoSaveEnabled],
  )

  const updatePagePreVersion = useCallback(
    async (values) => {
      const newValues = { ...values }
      const initialValues = { ...initialData }

      delete newValues.plainText
      delete initialValues.plainText
      if (!isEqual(initialValues, newValues)) {
        // В случае авто обновления нужно добавить к версии + 0.1,
        // но так, что бы версия не "округлилась
        const oldVersionArr = String(values.version).split(".")
        const newVersion = oldVersionArr[0] + "." + (Number(oldVersionArr[1] || 0) + 1)

        await updatePage(
          {
            ...values,
            version: newVersion,
          },
          false,
          null,
          shouldUpdateZonePages,
        )
        return newVersion
      }
    },
    [initialData, updatePage, shouldUpdateZonePages],
  )

  const getPageTitle = useCallback((form) => {
    return form?.getState()?.values?.title || null
  }, [])

  useEffect(() => {
    if (page?.id) {
      addPage(page)
    }
  })

  const handleMessage = useCallback(async (message) => {
    const data = JSON.parse(atob(message.data.replace(/"/g, ""))) || null
    if (data?.sender?.id !== user?.id && data?.text) {
      setUpdateMessage(data)
    }
  }, [])
  const [status] = useWebSocket(`${sseLink}?topics[]=page.${page?.id}`, handleMessage)
  //ws://localhost:8080/sse для локального теста

  return (
    <Form
      initialValues={initialData}
      validate={runValidation(validationSchema)}
      onSubmit={async (values) => {
        return await updatePage({
          ...values,
          version: isDraft ? "1" : String(Math.floor(Number(values.version) + 1)),
          status: statusList.find((status) => status.slug === PAGE_STATUSES.PUBLISH),
        })
      }}
      render={({ handleSubmit, values, errors, submitting, hasValidationErrors, form }) => {
        values.isAutoSaveEnabled &&
          form.subscribe(
            ({ values }) => {
              if (values.isAutoSaveEnabled && !isServerValidationError) {
                onChangeFormValues(values, hasValidationErrors, submitting, shouldUpdateZonePages)
              }
            },
            { values: true, submitting: true },
          )

        return (
          <>
            <PageBlockRow>
              <PageBlockRowSection
                flex="2"
                width="100%"
                maxWidth="1215px"
                flexBasis={["100%", "unset"]}
              >
                {updateMessage && (
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    gridGap="10px"
                    padding="5px 10px"
                    backgroundColor="#FFF5E8"
                    border="1px solid rgba(255, 245, 232, 1)"
                  >
                    <Text>
                      <Warning />{" "}
                    </Text>
                    <Text fontSize="14px">
                      <Link to={`/${ROUTES.PROFILE}/${updateMessage?.sender?.id}`}>
                        {CustomGetUsersOptionLabel(updateMessage?.sender)}
                      </Link>
                    </Text>

                    <Text fontSize="14px" color="gray-80">
                      {updateMessage?.text}
                    </Text>
                    {/* <Text fontSize="14px" color="gray-80">
                      {updateMessage.type === "wiki_page_update"
                        ? "опубликовал(а) новую версию страницы. Обновите страницу."
                        : "оставил (а) комментарий к этой странице. Обновите страницу"}
                    </Text> */}
                  </Box>
                )}
              </PageBlockRowSection>
            </PageBlockRow>
            <PageBlockContent
              id="editor_id"
              as="form"
              onSubmit={(e) => e.preventDefault()}
              style={{ marginTop: 0 }}
            >
              <Editor
                form={form}
                pageTitle={getPageTitle(form)}
                id="editor_ID"
                content={stringToEditorContent(values.text)}
                placeholder="Здесь напишите текст, который вы хотите добавить в документ"
                disabled={submitting}
                onChange={({ text, jsonText }) => {
                  form.change("text", jsonText)
                  form.change("plainText", text)
                  form.blur("text")
                }}
                errors={errors}
                values={values}
                submitting={submitting}
              />
            </PageBlockContent>
            <Box className={styles.bottomPanel}>
              <Text
                as={Button}
                disabled={isUpdateLoading || hasValidationErrors || !isAutoSaveEnabled}
                loading={isUpdateLoading}
                loadingMessage="Сохранение..."
                kind="primary"
                vertSize="32px"
                p="0 10px"
                height="32px"
                styles={{ fontSize: "14px" }}
                onClick={handleSubmit}
              >
                {isDraft ? "Опубликовать" : "Обновить"}
              </Text>
              <Text
                as={Button}
                kind="secondary"
                vertSize="32px"
                width="90px"
                height="32px"
                styles={{ fontSize: "14px" }}
                onClick={async () => {
                  await updatePagePreVersion(values)
                  navigate(`${ROUTES.ZONE}/${zone.slug}/pages/${page.id}`)
                }}
              >
                Закрыть
              </Text>
            </Box>
          </>
        )
      }}
    />
  )
})

export const PageForm = () => (
  <PageFormContextProvider>
    <PageFormForm />
  </PageFormContextProvider>
)

PageFormForm.displayName = "PageFormForm"
